import React, { useState, useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaHospital, FaMapMarkerAlt, FaPhone, FaClock, FaUser, FaEye, FaCalendarAlt, FaTimes } from 'react-icons/fa';
import { showErrorToast } from '../../../shared/utils/toastHelper';
import apiClient from '../../../shared/utils/apiConfig';

const DoctorClinics = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    const [clinics, setClinics] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setTitle('العيادات');
        fetchClinics();
    }, [setTitle]);

    const fetchClinics = async () => {
        try {
            setLoading(true);
            const response = await apiClient.get('/doctor/my_clinics');
            
            if (response.data && response.data.status === 'success') {
                const clinicsData = response.data.result || response.data.data;
                // التأكد من أن البيانات هي array
                if (Array.isArray(clinicsData)) {
                    setClinics(clinicsData);
                } else {
                    setClinics([]);
                }
            } else {
                throw new Error(response.data?.message || 'فشل في جلب العيادات');
            }
        } catch (error) {
            showErrorToast(error.response?.data?.message || error.message || 'فشل في جلب العيادات');
        } finally {
            setLoading(false);
        }
    };

    const formatWorkingHours = (workingHours) => {
        if (!workingHours) return 'غير محدد';
        return workingHours;
    };

    const handleViewWorkDays = (clinicId) => {
        navigate(`/doctor/work-days/${clinicId}`);
    };

    const handleViewEmptyDays = (clinicId) => {
        navigate(`/doctor/empty-days/${clinicId}`);
    };

    if (loading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">جاري تحميل العيادات...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container my-5">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">العيادات</h4>
                </div>

                {/* Clinics Table */}
                <div className="card shadow-lg border-0">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">جاري تحميل العيادات...</p>
                            </div>
                        ) : !Array.isArray(clinics) || clinics.length === 0 ? (
                            <div className="text-center py-5">
                                <FaHospital className="text-muted" style={{ fontSize: '3rem' }} />
                                <p className="mt-3 text-muted">لا توجد عيادات</p>
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-modern">
                                        <thead>
                                            <tr>
                                                <th className="text-center">#</th>
                                                <th className="text-center">اللوجو</th>
                                                <th className="text-center">اسم العيادة</th>
                                                <th className="text-center">رقم الهاتف</th>
                                                <th className="text-center">العنوان</th>
                                                <th className="text-center">عدد الأطباء</th>
                                                <th className="text-center">التقييم</th>
                                                <th className="text-center">أيام العمل</th>
                                                <th className="text-center">الأيام الفارغة</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {clinics.map((clinic, index) => (
                                                <motion.tr 
                                                    key={clinic.id || index}
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: index * 0.1 }}
                                                    className="table-row-modern">
                                                    <td className="text-center">
                                                        <div className="order-number-badge">
                                                            #{index + 1}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="clinic-logo-container">
                                                            {clinic.cover ? (
                                                                <img
                                                                    src={clinic.cover}
                                                                    alt={clinic.title}
                                                                    className="clinic-logo"
                                                                />
                                                            ) : (
                                                                <div className="default-placeholder">
                                                                    <FaHospital />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="clinic-name">
                                                            {clinic.title || 'غير محدد'}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="phone-badge">
                                                            {clinic.phone || 'غير محدد'}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="address-display">
                                                            {clinic.address || 'غير محدد'}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <span className="badge bg-info">
                                                            {clinic.doctors_count || 0}
                                                        </span>
                                                    </td>
                                                    <td className="text-center">
                                                        {clinic.rate_avg ? (
                                                            <div>
                                                                <span className="text-warning">⭐ {clinic.rate_avg}</span>
                                                                <small className="text-muted d-block">({clinic.rate_count})</small>
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted">لا يوجد</span>
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        <button
                                                            className="btn btn-sm"
                                                            onClick={() => handleViewWorkDays(clinic.id)}
                                                            title="أيام العمل"
                                                            style={{ backgroundColor: 'rgb(24, 65, 148)', color: 'white', border: 'none' }}>
                                                            <FaCalendarAlt className="mx-1" />
                                                            أيام العمل
                                                        </button>
                                                    </td>
                                                    <td className="text-center">
                                                        <button
                                                            className="btn btn-sm"
                                                            onClick={() => handleViewEmptyDays(clinic.id)}
                                                            title="الأيام الفارغة"
                                                            style={{ backgroundColor: '#6c757d', color: 'white', border: 'none' }}>
                                                            <FaTimes className="mx-1" />
                                                            غير نشطه
                                                        </button>
                                                    </td>
                                                </motion.tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </div>
                </div>

            </div>

            {/* CSS للتصميم */}
            <style>{`
                .table-modern {
                    border-collapse: separate;
                    border-spacing: 0;
                }
                
                .table-modern th {
                    background: transparent;
                    color: #374151;
                    font-weight: 600;
                    border: none;
                    padding: 15px 10px;
                    font-size: 0.9rem;
                    text-align: center;
                    position: relative;
                }
                
                .table-modern th::after {
                    content: '';
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    height: 2px;
                    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
                }
                
                .table-modern td {
                    padding: 15px 10px;
                    border-bottom: 1px solid #e9ecef;
                    vertical-align: middle;
                    text-align: center;
                }
                
                .table-row-modern:hover {
                    background-color: rgba(111, 66, 193, 0.08);
                    transform: translateY(-1px);
                    transition: all 0.2s ease;
                    box-shadow: 0 2px 8px rgba(111, 66, 193, 0.1);
                }
                
                .order-number-badge {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    padding: 0.5rem 1rem;
                    border-radius: 20px;
                    font-weight: 600;
                    font-size: 0.875rem;
                    display: inline-block;
                    min-width: 60px;
                    text-align: center;
                    box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
                }
                
                .clinic-logo-container {
                    width: 60px;
                    height: 60px;
                    margin: 0 auto;
                    border-radius: 8px;
                    overflow: hidden;
                    border: 2px solid #e9ecef;
                }
                
                .clinic-logo {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
                
                .default-placeholder {
                    width: 100%;
                    height: 100%;
                    background: #f8f9fa;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #6c757d;
                    font-size: 1.2rem;
                }
                
                .clinic-name {
                    font-weight: 500;
                    color: #374151;
                }
                
                .address-display {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                
                .phone-badge {
                    background: #e3f2fd;
                    color: #007bff;
                    padding: 4px 8px;
                    border-radius: 12px;
                    font-size: 0.8rem;
                    font-weight: 500;
                    border: 1px solid #007bff;
                }
                
                .badge {
                    font-size: 0.75rem;
                    padding: 0.4em 0.8em;
                }
                
                /* تقليل حجم عمود عدد الأطباء */
                .table-modern th:nth-child(6),
                .table-modern td:nth-child(6) {
                    width: 80px !important;
                    min-width: 80px !important;
                    max-width: 100px !important;
                }
            `}</style>
        </div>
    );
};

export default DoctorClinics;
